import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import apiService from '../../../services/apiService';
import { formatCurrency, formatDate } from '../../../utils/formatting';

/**
 * Click-through for the Photos and Quotes counts in the comps table.
 *
 * The counts were already there; the data behind them was not reachable. Both
 * badges open a small panel fed by the existing per-stock-number endpoints, so
 * a salesperson can see WHY a peer is drawing quotes without leaving the
 * comparison.
 *
 * The panel renders through a portal: the comps table sits in an
 * `overflow-x: auto` wrapper, which would clip an absolutely-positioned child.
 * Position is computed from the badge rect and flips above the badge when there
 * is no room below.
 */

const PANEL_W = 320;
const PANEL_MAX_H = 340;
const GAP = 6;

// ── Positioning ───────────────────────────────────────────────────────────────

function panelPosition(anchorRect) {
    const spaceBelow = window.innerHeight - anchorRect.bottom;
    const flipUp = spaceBelow < PANEL_MAX_H + GAP && anchorRect.top > spaceBelow;

    // Centre on the badge, then clamp so the panel never leaves the viewport.
    const rawLeft = anchorRect.left + anchorRect.width / 2 - PANEL_W / 2;
    const left = Math.max(8, Math.min(rawLeft, window.innerWidth - PANEL_W - 8));

    return flipUp
        ? { left, bottom: window.innerHeight - anchorRect.top + GAP }
        : { left, top: anchorRect.bottom + GAP };
}

// ── Shell ─────────────────────────────────────────────────────────────────────

/**
 * A badge that opens a panel on click and loads its content on first open.
 *
 * `load` is called once and its result cached, so re-opening the same badge is
 * free. A count of zero renders a flat, non-interactive badge: the endpoints
 * cannot distinguish "no data" from "no listing", and a panel that opens empty
 * is worse than one that never invites the click.
 */
function BadgePopover({ count, badgeClass, icon, title, label, load, children }) {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState(null);
    const [state, setState] = useState({ status: 'idle', data: null, error: null });
    const btnRef = useRef(null);
    const panelRef = useRef(null);
    const cache = useRef(null);

    const close = useCallback(() => setOpen(false), []);

    const toggle = useCallback(() => {
        if (open) return close();
        if (btnRef.current) setPos(panelPosition(btnRef.current.getBoundingClientRect()));
        setOpen(true);

        if (cache.current) {
            setState({ status: 'ready', data: cache.current, error: null });
            return;
        }
        setState({ status: 'loading', data: null, error: null });
        load()
            .then((data) => {
                cache.current = data;
                setState({ status: 'ready', data, error: null });
            })
            .catch((err) => setState({ status: 'error', data: null, error: err }));
    }, [open, close, load]);

    // Dismiss on outside click, Escape, scroll or resize. Scroll closes rather
    // than re-positions: the anchor can scroll out of its own container.
    useEffect(() => {
        if (!open) return undefined;

        const inside = (node) =>
            panelRef.current?.contains(node) || btnRef.current?.contains(node);

        const onPointerDown = (e) => {
            if (inside(e.target)) return;
            close();
        };
        const onKeyDown = (e) => {
            if (e.key !== 'Escape') return;
            // The detail pane also closes on Escape. Stop here so the first press
            // dismisses the panel and leaves the pane standing.
            e.stopPropagation();
            close();
            btnRef.current?.focus();
        };
        // The panel is anchored to a badge by viewport coordinates, so it has to
        // go when the page moves underneath it — but its own body scrolls, and
        // focusing a thumbnail can scroll that body. Only outside scrolls count.
        const onScroll = (e) => {
            if (inside(e.target)) return;
            close();
        };

        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', close);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', close);
        };
    }, [open, close]);

    if (!count) {
        return (
            <span className={`comps-photo-badge ${badgeClass}`}>
                {icon}
                {count ?? 0}
            </span>
        );
    }

    return (
        <>
            <button
                ref={btnRef}
                type="button"
                className={`comps-photo-badge comps-badge-btn ${badgeClass}`}
                onClick={toggle}
                aria-expanded={open}
                aria-haspopup="dialog"
                aria-label={`${count} ${count === 1 ? label.replace(/s$/, '') : label} — show details`}
            >
                {icon}
                {count}
            </button>

            {open && pos && createPortal(
                <div
                    ref={panelRef}
                    className="comps-pop"
                    style={{ ...pos, width: PANEL_W }}
                    role="dialog"
                    aria-label={title}
                    // Marks this as an overlay owned by the detail pane, so the
                    // pane's outside-click handler does not treat it as outside.
                    data-pane-overlay="true"
                >
                    <div className="comps-pop-head">
                        <span className="comps-pop-title">{title}</span>
                        <button type="button" className="comps-pop-close" onClick={close} aria-label="Close">
                            &times;
                        </button>
                    </div>
                    <div className="comps-pop-body">
                        {state.status === 'loading' && <div className="comps-pop-msg">Loading…</div>}
                        {state.status === 'error' && (
                            <div className="comps-pop-msg comps-pop-msg--error">Could not load this data.</div>
                        )}
                        {state.status === 'ready' && children(state.data)}
                    </div>
                </div>,
                document.body
            )}
        </>
    );
}

BadgePopover.propTypes = {
    count: PropTypes.number,
    badgeClass: PropTypes.string,
    icon: PropTypes.node,
    title: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    load: PropTypes.func.isRequired,
    children: PropTypes.func.isRequired,
};

// ── Icons ─────────────────────────────────────────────────────────────────────

const CameraIcon = (
    <svg viewBox="0 0 20 20" fill="currentColor" width="11" height="11" aria-hidden="true">
        <path fillRule="evenodd" d="M4 5a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-1.586a1 1 0 01-.707-.293l-1.121-1.121A2 2 0 0011.172 3H8.828a2 2 0 00-1.414.586L6.293 4.707A1 1 0 015.586 5H4zm6 9a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
    </svg>
);

const ChatIcon = (
    <svg viewBox="0 0 20 20" fill="currentColor" width="11" height="11" aria-hidden="true">
        <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
    </svg>
);

// ── Photos ────────────────────────────────────────────────────────────────────

/**
 * Thumbnail strip with an in-panel enlarged view.
 *
 * Deliberately self-contained rather than reusing the detail pane's lightbox —
 * that one is wired to the subject unit's image array, and pointing it at a
 * peer would couple the two.
 */
function PhotoGallery({ images, stockNumber, count }) {
    const [enlarged, setEnlarged] = useState(null);

    if (!images.length) {
        // apiService swallows request failures and returns an empty array, so an
        // empty result cannot be told apart from a genuine absence — except by
        // the badge, which only opens when the count says there is something.
        return (
            <div className="comps-pop-msg">
                {count > 0 ? 'Could not load these photos.' : 'No photos on this listing.'}
            </div>
        );
    }

    if (enlarged !== null) {
        const step = (delta) => setEnlarged((i) => (i + delta + images.length) % images.length);
        return (
            <div className="comps-pop-viewer">
                <img src={images[enlarged]} alt={`Stock ${stockNumber} — view ${enlarged + 1}`} />
                <div className="comps-pop-viewer-bar">
                    <button type="button" onClick={() => step(-1)} aria-label="Previous photo">‹</button>
                    <span>{enlarged + 1} / {images.length}</span>
                    <button type="button" onClick={() => step(1)} aria-label="Next photo">›</button>
                    <button type="button" className="comps-pop-viewer-back" onClick={() => setEnlarged(null)}>
                        All
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="comps-pop-thumbs">
            {images.map((url, i) => (
                <button
                    key={url + i}
                    type="button"
                    className="comps-pop-thumb"
                    onClick={() => setEnlarged(i)}
                    aria-label={`Enlarge photo ${i + 1}`}
                >
                    <img src={url} alt={`Stock ${stockNumber} — view ${i + 1}`} loading="lazy" />
                </button>
            ))}
        </div>
    );
}

PhotoGallery.propTypes = {
    images: PropTypes.arrayOf(PropTypes.string).isRequired,
    stockNumber: PropTypes.string.isRequired,
    count: PropTypes.number,
};

export function PeerPhotosBadge({ stockNumber, count, badgeClass }) {
    // getUnitImages already unwraps to the URL array.
    const load = useCallback(() => apiService.getUnitImages(stockNumber), [stockNumber]);

    return (
        <BadgePopover
            count={count}
            badgeClass={badgeClass}
            icon={CameraIcon}
            title={`Photos — #${stockNumber}`}
            label="photos"
            load={load}
        >
            {(images) => <PhotoGallery images={images} stockNumber={stockNumber} count={count} />}
        </BadgePopover>
    );
}

PeerPhotosBadge.propTypes = {
    stockNumber: PropTypes.string.isRequired,
    count: PropTypes.number,
    badgeClass: PropTypes.string,
};

// ── Market listing photos ─────────────────────────────────────────────────────

/**
 * Photos on an external market listing, served by the backend photo proxy.
 *
 * A plain <img src> cannot send the Bearer token, so every photo is fetched as
 * a Blob through apiService and rendered via an object URL. All object URLs are
 * revoked when the gallery unmounts — which BadgePopover does on every close,
 * so nothing leaks across opens. Photo 0 (the listing thumbnail) loads first so
 * the panel shows something quickly; the rest follow.
 */
function MarketPhotoGallery({ stockNumber, listingIndex, count, buildId }) {
    const [photos, setPhotos] = useState(() =>
        Array.from({ length: count }, () => ({ status: 'loading', url: null }))
    );
    const [enlarged, setEnlarged] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const created = [];

        const markError = (photoIndex) => {
            setPhotos((prev) => prev.map((p, i) => (i === photoIndex ? { status: 'error', url: null } : p)));
        };

        const loadOne = async (photoIndex) => {
            let url = null;
            try {
                // buildId binds the indexes to the payload they came from; a
                // stale build answers 404 -> null -> the muted fallback below.
                const blob = await apiService.getMarketCompsPhoto(stockNumber, listingIndex, photoIndex, buildId);
                if (blob instanceof Blob) url = URL.createObjectURL(blob);
            } catch (e) {
                url = null;
            }
            if (cancelled) {
                if (url) URL.revokeObjectURL(url);
                return;
            }
            if (!url) return markError(photoIndex);
            created.push(url);
            setPhotos((prev) => prev.map((p, i) => (i === photoIndex ? { status: 'ready', url } : p)));
        };

        (async () => {
            await loadOne(0);
            if (cancelled) return;
            await Promise.all(Array.from({ length: Math.max(0, count - 1) }, (_, i) => loadOne(i + 1)));
        })();

        return () => {
            cancelled = true;
            created.forEach((url) => URL.revokeObjectURL(url));
        };
    }, [stockNumber, listingIndex, count, buildId]);

    if (enlarged !== null) {
        const current = photos[enlarged];
        const step = (delta) => setEnlarged((i) => (i + delta + photos.length) % photos.length);
        return (
            <div className="comps-pop-viewer">
                {current.status === 'ready' ? (
                    <img
                        src={current.url}
                        alt={`Listing photo ${enlarged + 1}`}
                        onError={() => setEnlarged(null)}
                    />
                ) : (
                    <div className="comps-pop-msg">{current.status === 'loading' ? 'Loading…' : '—'}</div>
                )}
                <div className="comps-pop-viewer-bar">
                    <button type="button" onClick={() => step(-1)} aria-label="Previous photo">‹</button>
                    <span>{enlarged + 1} / {photos.length}</span>
                    <button type="button" onClick={() => step(1)} aria-label="Next photo">›</button>
                    <button type="button" className="comps-pop-viewer-back" onClick={() => setEnlarged(null)}>
                        All
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="comps-pop-thumbs">
            {photos.map((photo, i) => (
                photo.status === 'ready' ? (
                    <button
                        key={i}
                        type="button"
                        className="comps-pop-thumb"
                        onClick={() => setEnlarged(i)}
                        aria-label={`Enlarge photo ${i + 1}`}
                    >
                        <img
                            src={photo.url}
                            alt={`Listing photo ${i + 1}`}
                            loading="lazy"
                            // A blob that is not decodable image data: hide the img
                            // slot and show the muted dash instead.
                            onError={() =>
                                setPhotos((prev) =>
                                    prev.map((p, j) => (j === i ? { status: 'error', url: null } : p))
                                )
                            }
                        />
                    </button>
                ) : (
                    <span
                        key={i}
                        className="comps-pop-thumb comps-pop-thumb--placeholder"
                        aria-hidden="true"
                    >
                        {photo.status === 'loading' ? '' : '—'}
                    </span>
                )
            ))}
        </div>
    );
}

MarketPhotoGallery.propTypes = {
    stockNumber: PropTypes.string.isRequired,
    listingIndex: PropTypes.number.isRequired,
    count: PropTypes.number.isRequired,
    buildId: PropTypes.string,
};

export function MarketPhotosBadge({ stockNumber, listingIndex, count, badgeClass, buildId }) {
    // BadgePopover wants a data loader it can cache; the gallery loads its own
    // blobs (progressively, with revocation on close), so the loader is a no-op.
    const load = useCallback(() => Promise.resolve(null), []);

    return (
        <BadgePopover
            count={count}
            badgeClass={badgeClass}
            icon={CameraIcon}
            title="Listing photos"
            label="photos"
            load={load}
        >
            {() => (
                <MarketPhotoGallery
                    stockNumber={stockNumber}
                    listingIndex={listingIndex}
                    count={count}
                    buildId={buildId}
                />
            )}
        </BadgePopover>
    );
}

MarketPhotosBadge.propTypes = {
    stockNumber: PropTypes.string.isRequired,
    listingIndex: PropTypes.number.isRequired,
    count: PropTypes.number,
    badgeClass: PropTypes.string,
    buildId: PropTypes.string,
};

// ── Quotes ────────────────────────────────────────────────────────────────────

/**
 * What buyers actually offered on this peer.
 *
 * The gap against the peer's own asking price is the point of the panel — an
 * offer is only readable next to what was being asked.
 */
function QuoteList({ data, count }) {
    const quotes = data?.quotes || [];

    if (!quotes.length) {
        // Same caveat as the photo panel: a swallowed request failure arrives
        // here as an empty list, so trust the badge's count over the payload.
        return (
            <div className="comps-pop-msg">
                {count > 0 ? 'Could not load these quotes.' : 'No quotes recorded on this unit.'}
            </div>
        );
    }

    return (
        <>
            {data.asking_price != null && (
                <div className="comps-pop-sub">
                    Asking {formatCurrency(data.asking_price)}
                    {data.total_quotes > 0 && ` · ${data.total_quotes} quote${data.total_quotes === 1 ? '' : 's'}`}
                </div>
            )}
            <table className="comps-pop-table">
                <thead>
                    <tr>
                        <th>Date</th>
                        <th>Offered</th>
                        <th>vs Ask</th>
                        <th>Status</th>
                    </tr>
                </thead>
                <tbody>
                    {quotes.map((q) => {
                        const gap = q.price_gap_vs_asking;
                        return (
                            <tr key={q.quote_equipment_id}>
                                <td>{q.status_changed_date ? formatDate(q.status_changed_date) : '—'}</td>
                                <td>{formatCurrency(q.selling_price)}</td>
                                <td className={gap == null ? '' : gap < 0 ? 'comps-val-low' : gap > 0 ? 'comps-val-high' : ''}>
                                    {gap == null ? '—' : `${gap > 0 ? '+' : ''}${formatCurrency(gap)}`}
                                </td>
                                <td>{q.quote_status || '—'}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </>
    );
}

QuoteList.propTypes = { data: PropTypes.object, count: PropTypes.number };

export function PeerQuotesBadge({ stockNumber, count, badgeClass }) {
    const load = useCallback(() => apiService.getUnitQuoteEquipment(stockNumber), [stockNumber]);

    return (
        <BadgePopover
            count={count}
            badgeClass={badgeClass}
            icon={ChatIcon}
            title={`Quotes — #${stockNumber}`}
            label="quotes"
            load={load}
        >
            {(data) => <QuoteList data={data} count={count} />}
        </BadgePopover>
    );
}

PeerQuotesBadge.propTypes = {
    stockNumber: PropTypes.string.isRequired,
    count: PropTypes.number,
    badgeClass: PropTypes.string,
};
